import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
    adminFetch,
    getAdminToken,
    isAdminTokenExpired,
    clearAdminToken,
} from "./adminFetch";
import { AdminLayout } from "./components/AdminLayout";
import { StatsCards } from "./components/StatsCards";
import { BreakdownBars } from "./components/BreakdownBars";
import { CPLCalculator } from "./components/CPLCalculator";
import { LeadFilters } from "./components/LeadFilters";
import { LeadsTable } from "./components/LeadsTable";
import { LeadDetailDrawer } from "./components/LeadDetailDrawer";

const PAGE_LIMIT = 25;

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [authChecked, setAuthChecked] = useState(false);
    const [userId, setUserId] = useState("zenmegroup");

    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const [division, setDivision] = useState("all");
    const [status, setStatus] = useState("all");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);

    const [activeRow, setActiveRow] = useState(null);

    // Auth guard
    useEffect(() => {
        const token = getAdminToken();
        if (!token || isAdminTokenExpired()) {
            clearAdminToken();
            navigate("/admin/login", { replace: true });
            return;
        }
        (async () => {
            try {
                const me = await adminFetch("/admin/me");
                setUserId(me.userId || "zenmegroup");
                setAuthChecked(true);
            } catch (e) {
                navigate("/admin/login", { replace: true });
            }
        })();
    }, [navigate]);

    // Debounce search
    useEffect(() => {
        const h = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(h);
    }, [search]);

    // Reset page when filters change
    useEffect(() => {
        setPage(1);
    }, [division, status, debouncedSearch]);

    const fetchStats = useCallback(async () => {
        try {
            const s = await adminFetch("/admin/stats");
            setStats(s);
        } catch (e) {
            /* ignore */
        }
    }, []);

    const fetchLeads = useCallback(async () => {
        if (!authChecked) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                division,
                status,
                search: debouncedSearch,
                limit: String(PAGE_LIMIT),
                offset: String((page - 1) * PAGE_LIMIT),
                sort: "newest",
            });
            const data = await adminFetch(`/admin/leads?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            setItems([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [authChecked, division, status, debouncedSearch, page]);

    useEffect(() => {
        if (!authChecked) return;
        fetchStats();
        fetchLeads();
    }, [authChecked, fetchStats, fetchLeads]);

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(total / PAGE_LIMIT)),
        [total],
    );
    const showingFrom = total === 0 ? 0 : (page - 1) * PAGE_LIMIT + 1;
    const showingTo = Math.min(total, page * PAGE_LIMIT);

    const onClear = () => {
        setDivision("all");
        setStatus("all");
        setSearch("");
    };

    const onStatusChanged = () => {
        // refresh both the list and stats so the new status reflects
        fetchLeads();
        fetchStats();
    };

    if (!authChecked) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-base text-text-muted">
                <Loader2 className="h-5 w-5 animate-spin" />
            </div>
        );
    }

    return (
        <AdminLayout userId={userId}>
            <div className="space-y-8" data-testid="admin-dashboard">
                <header>
                    <p className="text-[11px] uppercase tracking-widest-2 text-text-muted">
                        Lead Operations
                    </p>
                    <h1 className="mt-2 font-display text-[clamp(24px,3vw,32px)] font-semibold tracking-tight-2 text-text-primary">
                        Dashboard
                    </h1>
                </header>

                <StatsCards stats={stats} />
                <BreakdownBars stats={stats} />
                <CPLCalculator />

                <section className="space-y-4">
                    <LeadFilters
                        division={division}
                        status={status}
                        search={search}
                        onDivisionChange={setDivision}
                        onStatusChange={setStatus}
                        onSearchChange={setSearch}
                        onClear={onClear}
                        showingFrom={showingFrom}
                        showingTo={showingTo}
                        total={total}
                    />
                    <LeadsTable
                        items={items}
                        loading={loading}
                        onRowClick={setActiveRow}
                        page={page}
                        totalPages={totalPages}
                        onPrev={() => setPage((p) => Math.max(1, p - 1))}
                        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                    />
                </section>
            </div>

            <LeadDetailDrawer
                row={activeRow}
                onClose={() => setActiveRow(null)}
                onStatusChanged={onStatusChanged}
            />
        </AdminLayout>
    );
}
